export type PermissionAction = 'view' | 'create' | 'edit' | 'delete' | 'approve' | 'reject' | 'export' | 'manage' | 'impersonate' | 'moderate' | 'publish' | 'configure';

export type PermissionEngine =
  | 'platform'
  | 'identity'
  | 'radio'
  | 'community'
  | 'content'
  | 'interaction'
  | 'notification'
  | 'discovery'
  | 'commerce'
  | 'advertising'
  | 'analytics'
  | 'ai';

export interface PermissionEntry {
  engine: PermissionEngine;
  actions: PermissionAction[];
}

export interface PermissionOverride {
  engine: PermissionEngine;
  action: PermissionAction;
  granted: boolean;
}

export interface RoleChangeLog {
  id: string;
  changedBy: string;
  changedAt: string;
  field: string;
  oldValue: string;
  newValue: string;
}

export interface SaRole {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  hierarchyLevel: number;
  active: boolean;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
  permissions: PermissionEntry[];
  overrides: PermissionOverride[];
  changeLog: RoleChangeLog[];
  userCount: number;
  criticalPermissions: string[];
}

export const ENGINE_LABELS: Record<PermissionEngine, string> = {
  platform: 'Platform Engine',
  identity: 'Identity Engine',
  radio: 'Radio Engine',
  community: 'Community Engine',
  content: 'Content Engine',
  interaction: 'Interaction Engine',
  notification: 'Notification Engine',
  discovery: 'Discovery Engine',
  commerce: 'Commerce Engine',
  advertising: 'Advertising Engine',
  analytics: 'Analytics Engine',
  ai: 'AI Engine',
};

export const ACTION_LABELS: Record<PermissionAction, string> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  approve: 'Approve',
  reject: 'Reject',
  export: 'Export',
  manage: 'Manage',
  impersonate: 'Impersonate',
  moderate: 'Moderate',
  publish: 'Publish',
  configure: 'Configure',
};

export const ALL_ACTIONS: PermissionAction[] = ['view', 'create', 'edit', 'delete', 'approve', 'reject', 'export', 'manage', 'impersonate', 'moderate', 'publish', 'configure'];

export const ALL_ENGINES: PermissionEngine[] = ['platform', 'identity', 'radio', 'community', 'content', 'interaction', 'notification', 'discovery', 'commerce', 'advertising', 'analytics', 'ai'];

export const CRITICAL_ACTIONS: PermissionAction[] = ['delete', 'manage', 'impersonate', 'configure'];

export const saRoles: SaRole[] = [
  {
    id: 'role-001',
    name: 'Super Admin',
    slug: 'super-admin',
    description: 'Acceso total a todas las funcionalidades de la plataforma. Control absoluto sobre configuración, usuarios y datos del sistema.',
    icon: 'ri-shield-star-line',
    color: '#0f172a',
    hierarchyLevel: 1,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-07-12',
    permissions: ALL_ENGINES.map((engine) => ({ engine, actions: [...ALL_ACTIONS] })),
    overrides: [],
    changeLog: [
      { id: 'log-001-a', changedBy: 'superadmin@rfw.com', changedAt: '2026-07-10', field: 'permissions.ai', oldValue: 'Sin acceso a AI Engine', newValue: 'Acceso total a AI Engine' },
    ],
    userCount: 3,
    criticalPermissions: ['delete', 'manage', 'impersonate', 'configure'],
  },
  {
    id: 'role-002',
    name: 'Platform Admin',
    slug: 'platform-admin',
    description: 'Administración completa de la plataforma. Gestiona radios, usuarios, planes y configuración global excepto acciones destructivas del sistema.',
    icon: 'ri-settings-4-line',
    color: '#1e40af',
    hierarchyLevel: 2,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-06-28',
    permissions: [
      { engine: 'platform', actions: ['view', 'edit', 'manage', 'configure', 'export'] },
      { engine: 'identity', actions: ['view', 'create', 'edit', 'delete', 'manage', 'export'] },
      { engine: 'radio', actions: ['view', 'create', 'edit', 'delete', 'approve', 'reject', 'manage', 'export', 'configure'] },
      { engine: 'community', actions: ['view', 'edit', 'delete', 'moderate', 'manage', 'export'] },
      { engine: 'content', actions: ['view', 'edit', 'delete', 'publish', 'moderate', 'manage', 'export'] },
      { engine: 'interaction', actions: ['view', 'edit', 'delete', 'moderate', 'manage', 'export'] },
      { engine: 'notification', actions: ['view', 'create', 'edit', 'publish', 'manage', 'configure', 'export'] },
      { engine: 'discovery', actions: ['view', 'edit', 'publish', 'manage', 'configure', 'export'] },
      { engine: 'commerce', actions: ['view', 'approve', 'reject', 'manage', 'export'] },
      { engine: 'advertising', actions: ['view', 'create', 'edit', 'approve', 'reject', 'manage', 'export'] },
      { engine: 'analytics', actions: ['view', 'export', 'manage'] },
      { engine: 'ai', actions: ['view', 'configure', 'manage'] },
    ],
    overrides: [
      { engine: 'platform', action: 'delete', granted: false },
      { engine: 'commerce', action: 'delete', granted: false },
    ],
    changeLog: [
      { id: 'log-002-a', changedBy: 'superadmin@rfw.com', changedAt: '2026-06-01', field: 'permissions.ai', oldValue: 'Sin acceso', newValue: 'View + Configure + Manage' },
    ],
    userCount: 5,
    criticalPermissions: ['delete', 'manage', 'configure'],
  },
  {
    id: 'role-003',
    name: 'Support Agent',
    slug: 'support-agent',
    description: 'Soporte a radios y oyentes. Accede a tickets, datos de usuarios y herramientas de diagnóstico sin modificar configuración del sistema.',
    icon: 'ri-mental-health-line',
    color: '#0d9488',
    hierarchyLevel: 3,
    active: true,
    isSystem: true,
    createdAt: '2024-02-10',
    updatedAt: '2026-07-01',
    permissions: [
      { engine: 'platform', actions: ['view'] },
      { engine: 'identity', actions: ['view', 'edit'] },
      { engine: 'radio', actions: ['view'] },
      { engine: 'community', actions: ['view'] },
      { engine: 'content', actions: ['view'] },
      { engine: 'interaction', actions: ['view'] },
      { engine: 'notification', actions: ['view', 'create'] },
      { engine: 'discovery', actions: ['view'] },
      { engine: 'commerce', actions: ['view'] },
      { engine: 'advertising', actions: ['view'] },
      { engine: 'analytics', actions: ['view'] },
      { engine: 'ai', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 12,
    criticalPermissions: [],
  },
  {
    id: 'role-004',
    name: 'Billing Manager',
    slug: 'billing-manager',
    description: 'Gestión financiera completa. Administra facturación, pagos, contratos, licencias y revenue share de todas las radios.',
    icon: 'ri-bank-card-line',
    color: '#ca8a04',
    hierarchyLevel: 3,
    active: true,
    isSystem: true,
    createdAt: '2024-02-15',
    updatedAt: '2026-06-15',
    permissions: [
      { engine: 'platform', actions: ['view'] },
      { engine: 'identity', actions: ['view'] },
      { engine: 'radio', actions: ['view'] },
      { engine: 'commerce', actions: ['view', 'create', 'edit', 'approve', 'reject', 'export', 'manage'] },
      { engine: 'advertising', actions: ['view', 'approve', 'export'] },
      { engine: 'analytics', actions: ['view', 'export'] },
    ],
    overrides: [],
    changeLog: [
      { id: 'log-004-a', changedBy: 'superadmin@rfw.com', changedAt: '2026-05-20', field: 'permissions.commerce', oldValue: 'View + Approve + Export', newValue: 'Control total del Commerce Engine' },
    ],
    userCount: 4,
    criticalPermissions: ['manage'],
  },
  {
    id: 'role-005',
    name: 'Content Moderator',
    slug: 'content-moderator',
    description: 'Moderación de contenido global. Revisa, aprueba, rechaza y elimina contenido en todas las radios y la comunidad.',
    icon: 'ri-shield-check-line',
    color: '#7c3aed',
    hierarchyLevel: 3,
    active: true,
    isSystem: true,
    createdAt: '2024-03-01',
    updatedAt: '2026-06-20',
    permissions: [
      { engine: 'community', actions: ['view', 'edit', 'delete', 'moderate'] },
      { engine: 'content', actions: ['view', 'edit', 'delete', 'approve', 'reject', 'moderate', 'publish'] },
      { engine: 'interaction', actions: ['view', 'edit', 'delete', 'moderate'] },
      { engine: 'notification', actions: ['view', 'create'] },
      { engine: 'radio', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 8,
    criticalPermissions: ['delete', 'moderate'],
  },
  {
    id: 'role-006',
    name: 'Radio Owner',
    slug: 'radio-owner',
    description: 'Dueño de radio con control total sobre su emisora. Configura estación, equipo, monetización y contenido.',
    icon: 'ri-radio-line',
    color: '#dc2626',
    hierarchyLevel: 4,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-07-05',
    permissions: [
      { engine: 'radio', actions: ['view', 'create', 'edit', 'delete', 'manage', 'configure', 'export'] },
      { engine: 'identity', actions: ['view', 'create', 'edit', 'delete', 'manage'] },
      { engine: 'community', actions: ['view', 'create', 'edit', 'delete', 'moderate', 'publish', 'manage'] },
      { engine: 'content', actions: ['view', 'create', 'edit', 'delete', 'publish', 'manage'] },
      { engine: 'interaction', actions: ['view', 'create', 'edit', 'delete', 'moderate'] },
      { engine: 'notification', actions: ['view', 'create', 'edit', 'publish', 'manage'] },
      { engine: 'commerce', actions: ['view'] },
      { engine: 'advertising', actions: ['view', 'create', 'edit'] },
      { engine: 'analytics', actions: ['view', 'export'] },
      { engine: 'discovery', actions: ['view', 'edit'] },
    ],
    overrides: [],
    changeLog: [
      { id: 'log-006-a', changedBy: 'platform@rfw.com', changedAt: '2026-07-05', field: 'permissions.advertising', oldValue: 'Solo View', newValue: 'View + Create + Edit' },
    ],
    userCount: 189,
    criticalPermissions: ['delete', 'manage'],
  },
  {
    id: 'role-007',
    name: 'Radio Admin',
    slug: 'radio-admin',
    description: 'Administrador de una radio específica. Gestiona contenido, equipo y operaciones diarias sin acceso a eliminación de la estación.',
    icon: 'ri-user-settings-line',
    color: '#ea580c',
    hierarchyLevel: 5,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-05-10',
    permissions: [
      { engine: 'radio', actions: ['view', 'edit', 'configure', 'export'] },
      { engine: 'identity', actions: ['view', 'edit'] },
      { engine: 'community', actions: ['view', 'create', 'edit', 'moderate', 'publish'] },
      { engine: 'content', actions: ['view', 'create', 'edit', 'delete', 'publish'] },
      { engine: 'interaction', actions: ['view', 'edit', 'moderate'] },
      { engine: 'notification', actions: ['view', 'create', 'edit', 'publish'] },
      { engine: 'advertising', actions: ['view', 'edit'] },
      { engine: 'analytics', actions: ['view', 'export'] },
      { engine: 'commerce', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 412,
    criticalPermissions: ['delete', 'moderate'],
  },
  {
    id: 'role-008',
    name: 'Radio Staff',
    slug: 'radio-staff',
    description: 'Personal de radio con acceso operativo. Publica contenido, modera chat y gestiona programas asignados.',
    icon: 'ri-team-line',
    color: '#0891b2',
    hierarchyLevel: 6,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-04-22',
    permissions: [
      { engine: 'radio', actions: ['view'] },
      { engine: 'community', actions: ['view', 'create', 'edit', 'moderate'] },
      { engine: 'content', actions: ['view', 'create', 'edit', 'publish'] },
      { engine: 'interaction', actions: ['view', 'create', 'edit'] },
      { engine: 'notification', actions: ['view', 'create'] },
      { engine: 'analytics', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 627,
    criticalPermissions: ['moderate'],
  },
  {
    id: 'role-009',
    name: 'Host',
    slug: 'host',
    description: 'Locutor y presentador. Accede a su perfil público, programas asignados, cabina de transmisión y analíticas de su audiencia.',
    icon: 'ri-user-voice-line',
    color: '#a21caf',
    hierarchyLevel: 7,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-03-15',
    permissions: [
      { engine: 'radio', actions: ['view'] },
      { engine: 'community', actions: ['view', 'create'] },
      { engine: 'content', actions: ['view', 'create', 'edit', 'publish'] },
      { engine: 'interaction', actions: ['view', 'create'] },
      { engine: 'notification', actions: ['view', 'create'] },
      { engine: 'analytics', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 891,
    criticalPermissions: [],
  },
  {
    id: 'role-010',
    name: 'Listener',
    slug: 'listener',
    description: 'Oyente registrado. Escucha radios, interactúa en comunidad, participa en gamificación y gestiona sus favoritos.',
    icon: 'ri-user-line',
    color: '#059669',
    hierarchyLevel: 8,
    active: true,
    isSystem: true,
    createdAt: '2024-01-15',
    updatedAt: '2026-07-10',
    permissions: [
      { engine: 'radio', actions: ['view'] },
      { engine: 'community', actions: ['view', 'create'] },
      { engine: 'content', actions: ['view'] },
      { engine: 'interaction', actions: ['view', 'create'] },
      { engine: 'notification', actions: ['view'] },
      { engine: 'discovery', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [
      { id: 'log-010-a', changedBy: 'platform@rfw.com', changedAt: '2026-07-10', field: 'permissions.discovery', oldValue: 'Sin acceso', newValue: 'View Discovery Engine' },
    ],
    userCount: 45230,
    criticalPermissions: [],
  },
  {
    id: 'role-011',
    name: 'Advertiser',
    slug: 'advertiser',
    description: 'Anunciante registrado. Crea y gestiona campañas publicitarias, consulta métricas y administra su presupuesto.',
    icon: 'ri-advertisement-line',
    color: '#b45309',
    hierarchyLevel: 8,
    active: true,
    isSystem: true,
    createdAt: '2024-02-01',
    updatedAt: '2026-05-18',
    permissions: [
      { engine: 'advertising', actions: ['view', 'create', 'edit'] },
      { engine: 'commerce', actions: ['view'] },
      { engine: 'analytics', actions: ['view'] },
      { engine: 'notification', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 312,
    criticalPermissions: [],
  },
  {
    id: 'role-012',
    name: 'Ambassador',
    slug: 'ambassador',
    description: 'Embajador RFW. Promociona la plataforma, participa en eventos especiales y accede a contenido exclusivo.',
    icon: 'ri-vip-crown-line',
    color: '#e11d48',
    hierarchyLevel: 7,
    active: true,
    isSystem: true,
    createdAt: '2024-04-10',
    updatedAt: '2026-06-05',
    permissions: [
      { engine: 'community', actions: ['view', 'create'] },
      { engine: 'content', actions: ['view', 'create'] },
      { engine: 'interaction', actions: ['view', 'create'] },
      { engine: 'notification', actions: ['view'] },
      { engine: 'discovery', actions: ['view'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 47,
    criticalPermissions: [],
  },
  {
    id: 'role-013',
    name: 'Moderator',
    slug: 'moderator',
    description: 'Moderador comunitario. Supervisa contenido de la comunidad, resuelve reportes y mantiene el orden en chats y foros.',
    icon: 'ri-scales-3-line',
    color: '#4f46e5',
    hierarchyLevel: 6,
    active: true,
    isSystem: true,
    createdAt: '2024-03-01',
    updatedAt: '2026-04-30',
    permissions: [
      { engine: 'community', actions: ['view', 'edit', 'delete', 'moderate'] },
      { engine: 'content', actions: ['view', 'edit', 'delete', 'moderate'] },
      { engine: 'interaction', actions: ['view', 'edit', 'delete', 'moderate'] },
      { engine: 'notification', actions: ['view', 'create'] },
    ],
    overrides: [],
    changeLog: [],
    userCount: 156,
    criticalPermissions: ['delete', 'moderate'],
  },
];

export interface SaRolesPermissionsState {
  systemStatus: 'healthy' | 'warning';
  lastAuditAt: string;
  totalActiveRoles: number;
  totalDisabledRoles: number;
  totalPermissionsConfigured: number;
  criticalPermissionsActive: number;
}

export const saRolesPermissionsState: SaRolesPermissionsState = {
  systemStatus: 'healthy',
  lastAuditAt: '2026-07-12T08:45:00Z',
  totalActiveRoles: 13,
  totalDisabledRoles: 0,
  totalPermissionsConfigured: 1207,
  criticalPermissionsActive: 27,
};